import React, { ChangeEvent } from "react";
import {
  FormHelperText,
  FormLabel,
  Grid,
  Radio as MuiRadio,
  RadioGroup,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { styled } from "@mui/material/styles";
import { useModalStyles } from "./useModalStyles";

type Option = {
  value: any;
  label: string;
  description?: string;
  icon?: JSX.Element;
  disabled?: boolean;
};

type ColorVariant = "success" | "error" | "info" | "warning";

interface ModernStyledRadioProps {
  name?: string;
  options?: Option[];
  label?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  control: Control<any, any>;
  isDisabled?: boolean;
  rules?: object;
  controllerName?: string;
  defaultValue?: string | boolean;
  gridSize?: {
    xs?: number;
    sm?: number;
    md?: number;
  };
  color?: ColorVariant;
}

const colorConfig = {
  success: {
    light: {
      gradient: "linear-gradient(135deg, #81C784 0%, #66BB6A 100%)",
      border: "#4CAF50",
    },
    dark: {
      gradient: "linear-gradient(135deg, #2D5939 0%, #1B5E20 100%)",
      border: "#2D5939",
    },
  },
  error: {
    light: {
      gradient: "linear-gradient(135deg, #E57373 0%, #EF5350 100%)",
      border: "#F44336",
    },
    dark: {
      gradient: "linear-gradient(135deg, #C62828 0%, #B71C1C 100%)",
      border: "#C62828",
    },
  },
  info: {
    light: {
      gradient: "linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)",
      border: "#2196F3",
    },
    dark: {
      gradient: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
      border: "#1565C0",
    },
  },
  warning: {
    light: {
      gradient: "linear-gradient(135deg, #FFB74D 0%, #FFA726 100%)",
      border: "#FF9800",
    },
    dark: {
      gradient: "linear-gradient(135deg, #E65100 0%, #BF360C 100%)",
      border: "#E65100",
    },
  },
};

const StyledRadio = styled(MuiRadio)(() => ({
  padding: 4,
}));

const ModernStyledRadio: React.FC<ModernStyledRadioProps> = (props) => {
  const {
    name,
    options,
    label,
    onChange,
    control,
    isDisabled,
    rules,
    controllerName,
    defaultValue,
    gridSize = { xs: 12, sm: 6, md: 4 },
    color = "success",
  } = props;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { tokens: mTokens, isDark } = useModalStyles();

  // "success" (the default/only variant in use) is mapped to the app's teal
  // accent so this matches SectionTabNav/cardSx elsewhere, instead of a
  // generic Material green. Other variants keep their original semantic hue.
  const accentHex = color === "success" ? mTokens.accent : colorConfig[color][isDark ? "dark" : "light"].border;

  return (
    <Controller
      name={controllerName || ""}
      control={control}
      rules={rules || {}}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => (
        <>
          {label && (
            <FormLabel
              component="legend"
              sx={{
                color: fieldState.error ? theme.palette.error.main : "inherit",
                marginBottom: "8px",
              }}
            >
              {label}
            </FormLabel>
          )}
          <RadioGroup
            defaultValue={defaultValue}
            name={name || controllerName}
            onChange={(e) => {
              field.onChange(e.target.value);
              onChange && onChange(e);
            }}
          >
            <Grid container spacing={2}>
              {options?.map((option) => {
                const isSelected = field.value === option.value;
                const hasDescription = !!option.description;
                const isOptionDisabled = isDisabled || option.disabled;

                return (
                  <Grid
                    item
                    xs={gridSize.xs}
                    sm={gridSize.sm}
                    md={gridSize.md}
                    key={option.value}
                  >
                    <Box
                      onClick={() => {
                        if (!isOptionDisabled) {
                          field.onChange(option.value);
                        }
                      }}
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "14px",
                        border: "1.5px solid",
                        borderColor: isSelected ? accentHex : mTokens.border,
                        backgroundColor: isSelected ? `${accentHex}14` : mTokens.bg,
                        boxShadow: isSelected ? `0 0 0 3px ${accentHex}1f` : "none",
                        cursor: isOptionDisabled ? "not-allowed" : "pointer",
                        transition: "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
                        height: "100%",
                        opacity: isOptionDisabled && !isSelected ? 0.4 : 1,

                        "&:hover": {
                          borderColor: accentHex,
                        },
                      }}
                    >
                      {/* Icon Container */}
                      {option.icon && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            backgroundColor: `${accentHex}1a`,
                            border: `1px solid ${accentHex}33`,
                            color: accentHex,
                            flexShrink: 0,
                            "& svg": {
                              fontSize: 18,
                            },
                          }}
                        >
                          {option.icon}
                        </Box>
                      )}

                      {/* Content */}
                      <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: isSelected ? accentHex : mTokens.textPrimary,
                            mb: hasDescription ? 0.5 : 0,
                            transition: "color 0.18s ease",
                          }}
                        >
                          {option.label}
                        </Typography>
                        {hasDescription && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: mTokens.textSecondary,
                              lineHeight: 1.5,
                            }}
                          >
                            {option.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Radio Button */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <StyledRadio
                          value={option.value}
                          checked={isSelected}
                          disabled={isOptionDisabled}
                          sx={{
                            color: isDarkMode ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.3)",
                            "&.Mui-checked": {
                              color: accentHex,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
          {fieldState.error && (
            <FormHelperText error>{fieldState.error.message}</FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default ModernStyledRadio;
